/* eslint-disable import/no-cycle */
import type { OriginalRootState } from 'redux/rootReducer'
import { PayloadAction, createSlice, isAnyOf } from '@reduxjs/toolkit'
import { RegistrationState } from 'typings/reduxStore'
import { getBusinessError, isServerError } from 'helpers/parseError'
import responseCodes from 'const/responseCodes'
import {
  checkRegistrationEligibility,
  saveFirstTimeLogin,
  updateChildPrivilege
} from './async-thunks'

export const initState: RegistrationState = {
  error: false,
  errorMessage: '',
  isEligibilitySuccess: false,
  isEligibilityLoading: false,
  sessionId: '',
  status: '',
  isRegisteredSuccess: false,
  accountIndicator: '',
  isPrivilegeSuccess: false,
  isAccountError: false,
  isAuthCodeError: false,
  accountType: ''
}

const registrationSlice = createSlice({
  name: 'registrationSlice',
  initialState: initState,
  reducers: {
    updateRegistrationState: (
      state: RegistrationState,
      action: PayloadAction<RegistrationState>
    ) => {
      return {
        ...state,
        ...action.payload
      }
    },
    resetAuthenticationErrorState: (state: RegistrationState) => {
      state.error = false
      state.errorMessage = ''
      state.isAuthCodeError = false
    },
    completeRegistration: (
      state: RegistrationState,
      action: PayloadAction<RegistrationState>
    ) => {
      state.error = false
      state.errorMessage = ''
      state.isRegisteredSuccess = true
      state.accountIndicator = action.payload.accountIndicator
    },
    clearError: state => {
      state.error = false
      state.errorMessage = ''
      state.isAccountError = false
      state.isAuthCodeError = false
    },
    /**
     * clears the registration success state while going back to the login screen
     */
    clearRegistrationSuccessState: state => {
      state.isRegisteredSuccess = false
    }
  },
  extraReducers: builder => {
    builder
      .addCase(updateChildPrivilege.fulfilled, state => {
        state.error = false
        state.errorMessage = ''
        state.isPrivilegeSuccess = true
      })
      .addCase(updateChildPrivilege.rejected, (state, { payload }) => {
        state.error = true
        state.isPrivilegeSuccess = false

        if (isServerError(payload)) {
          state.errorMessage = payload.response.message
        }
      })
      .addCase(saveFirstTimeLogin.fulfilled, state => {
        state.error = false
        state.errorMessage = ''
        state.isRegisteredSuccess = true
      })
      .addCase(checkRegistrationEligibility.pending, state => {
        state.isEligibilityLoading = true
      })
      /*
       * Using the isAnyOf matcher here because there are several thunks that
       * should be refactored to use this reducer instead of the "onSaveFailure"
       * method found in the async-thunks file.
       */
      .addMatcher(isAnyOf(saveFirstTimeLogin.rejected), (state, action) => {
        state.isRegisteredSuccess = false
        state.accountIndicator = ''
        state.isAccountError = false
        state.error = true

        const busError =
          isServerError(action.payload) && getBusinessError(action.payload)

        if (!busError) {
          state.errorMessage = ''
          return
        }

        const { code = '', message = '' } = busError

        state.errorMessage = message

        if (code === responseCodes.accountMismatch) {
          state.isAccountError = true
        }
      })
      .addMatcher(
        isAnyOf(
          checkRegistrationEligibility.fulfilled,
          checkRegistrationEligibility.rejected
        ),
        state => {
          state.isEligibilityLoading = false
        }
      )
  }
})

export const {
  updateRegistrationState,
  resetAuthenticationErrorState,
  completeRegistration,
  clearError,
  clearRegistrationSuccessState
} = registrationSlice.actions

/** Returns the pending state of the registration eligibility call */
export const selectIsEligibilityLoading = (state: OriginalRootState) =>
  state.registration.isEligibilityLoading

/**
 * Returns the sessionId from the registration slice.
 */
export const selectSessionId = (state: OriginalRootState) =>
  state.registration.sessionId

export default registrationSlice.reducer
